import React, { useRef, useState } from 'react';
import {
  Box,
  Paper,
  Typography,
  Button,
  IconButton,
  Card,
  CardContent,
  CardMedia,
  Alert,
} from '@mui/material';
import {
  CloudUpload,
  CameraAlt,
  InsertDriveFile,
  Close,
  Visibility,
} from '@mui/icons-material';

const ReceiptUploader = ({ receipt, onReceiptUpload }) => {
  const fileInputRef = useRef(null);
  const [preview, setPreview] = useState(null);
  const [error, setError] = useState('');

  const handleFileSelect = (e) => {
    const file = e.target.files?.[0];
    if (file) {
      validateAndUpload(file);
    }
  };

  const validateAndUpload = (file) => {
    setError('');

    // Check file size (max 5MB)
    const maxSize = 5 * 1024 * 1024; // 5MB
    if (file.size > maxSize) {
      setError('File size must be less than 5MB');
      return;
    }

    // Check file type
    const allowedTypes = ['image/jpeg', 'image/png', 'image/jpg', 'application/pdf'];
    if (!allowedTypes.includes(file.type)) {
      setError('Only JPG, PNG, and PDF files are allowed');
      return;
    }

    // Create preview for images
    if (file.type.startsWith('image/')) {
      const reader = new FileReader();
      reader.onloadend = () => {
        setPreview(reader.result);
      };
      reader.readAsDataURL(file);
    } else {
      setPreview(null); // No preview for PDFs
    }

    onReceiptUpload(file);
  };

  const handleRemove = () => {
    setPreview(null);
    setError('');
    onReceiptUpload(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const formatFileSize = (bytes) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return Math.round(bytes / Math.pow(k, i) * 100) / 100 + ' ' + sizes[i];
  };

  if (receipt) {
    // Show uploaded file
    return (
      <Card variant="outlined">
        <CardContent>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            {preview ? (
              // Image preview
              <CardMedia
                component="img"
                sx={{
                  width: 100,
                  height: 100,
                  objectFit: 'cover',
                  borderRadius: 1,
                  border: 1,
                  borderColor: 'divider'
                }}
                image={preview}
                alt="Receipt preview"
              />
            ) : (
              // PDF or other file icon
              <Box
                sx={{
                  width: 100,
                  height: 100,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  bgcolor: 'grey.100',
                  borderRadius: 1,
                  border: 1,
                  borderColor: 'divider'
                }}
              >
                <InsertDriveFile sx={{ fontSize: 48, color: 'text.secondary' }} />
              </Box>
            )}

            <Box sx={{ flex: 1 }}>
              <Typography variant="body1" fontWeight={600} gutterBottom>
                {receipt.name}
              </Typography>
              <Typography variant="caption" color="text.secondary" display="block">
                Size: {formatFileSize(receipt.size)}
              </Typography>
              <Typography variant="caption" color="text.secondary" display="block">
                Type: {receipt.type}
              </Typography>

              <Box sx={{ mt: 2, display: 'flex', gap: 1 }}>
                {preview && (
                  <Button
                    size="small"
                    startIcon={<Visibility />}
                    onClick={() => window.open(preview, '_blank')}
                  >
                    Preview
                  </Button>
                )}
                <Button
                  size="small"
                  color="error"
                  startIcon={<Close />}
                  onClick={handleRemove}
                >
                  Remove
                </Button>
              </Box>
            </Box>
          </Box>
        </CardContent>
      </Card>
    );
  }

  // Show upload UI
  return (
    <Box>
      <input
        ref={fileInputRef}
        type="file"
        accept="image/jpeg,image/png,image/jpg,application/pdf"
        onChange={handleFileSelect}
        style={{ display: 'none' }}
      />

      <Paper
        variant="outlined"
        sx={{
          p: 4,
          textAlign: 'center',
          bgcolor: 'grey.50',
          border: '2px dashed',
          borderColor: error ? 'error.main' : 'divider',
          cursor: 'pointer',
          transition: 'all 0.2s',
          '&:hover': {
            borderColor: 'primary.main',
            bgcolor: 'primary.50',
          }
        }}
        onClick={() => fileInputRef.current?.click()}
      >
        <CloudUpload sx={{ fontSize: 48, color: 'primary.main', mb: 2 }} />

        <Typography variant="h6" gutterBottom>
          Upload Receipt or Deposit Slip
        </Typography>

        <Typography variant="body2" color="text.secondary" gutterBottom>
          Click to browse or drag and drop
        </Typography>

        <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 2 }}>
          Supported formats: JPG, PNG, PDF (Max 5MB)
        </Typography>

        <Box sx={{ mt: 3, display: 'flex', gap: 2, justifyContent: 'center' }}>
          <Button
            variant="contained"
            startIcon={<CloudUpload />}
            onClick={(e) => {
              e.stopPropagation();
              fileInputRef.current?.click();
            }}
          >
            Choose File
          </Button>

          {/* Camera upload - only on mobile */}
          {/Android|iPhone|iPad|iPod/i.test(navigator.userAgent) && (
            <Button
              variant="outlined"
              startIcon={<CameraAlt />}
              onClick={(e) => {
                e.stopPropagation();
                // Open camera on mobile
                const input = document.createElement('input');
                input.type = 'file';
                input.accept = 'image/*';
                input.capture = 'environment';
                input.onchange = (event) => {
                  const file = event.target.files?.[0];
                  if (file) {
                    validateAndUpload(file);
                  }
                };
                input.click();
              }}
            >
              Take Photo
            </Button>
          )}
        </Box>
      </Paper>

      {error && (
        <Alert severity="error" sx={{ mt: 2 }}>
          {error}
        </Alert>
      )}
    </Box>
  );
};

export default ReceiptUploader;
